(() => {
  'use strict';

  const root = document.querySelector('[data-techtic-chat]');
  if (!root || root.dataset.initialized === 'true') return;
  root.dataset.initialized = 'true';

  const panel = root.querySelector('[data-chat-panel]');
  const openButton = root.querySelector('[data-chat-open]');
  const closeButton = root.querySelector('[data-chat-close]');
  const clearButton = root.querySelector('[data-chat-clear]');
  const messagesNode = root.querySelector('[data-chat-messages]');
  const suggestionsNode = root.querySelector('[data-chat-suggestions]');
  const form = root.querySelector('[data-chat-form]');
  const input = root.querySelector('[data-chat-input]');
  const sendButton = root.querySelector('[data-chat-send]');
  const cartNode = root.querySelector('[data-chat-cart]');
  const cartCountNode = root.querySelector('[data-chat-cart-count]');
  const cartTotalNode = root.querySelector('[data-chat-cart-total]');
  const cartViewLink = root.querySelector('[data-chat-cart-view]');
  const checkoutLink = root.querySelector('[data-chat-checkout]');
  const announcer = root.querySelector('[data-chat-announcer]');
  if (!panel || !openButton || !closeButton || !messagesNode || !suggestionsNode || !form || !input || !sendButton) return;

  const EVENTS = Object.freeze({
    OPEN: 'chat_opened',
    MESSAGE: 'chat_message_sent',
    STREAM_STARTED: 'stream_started',
    STREAM_FALLBACK: 'stream_fallback',
    RESPONSE: 'response',
    RECOMMENDATION_IMPRESSION: 'recommendation_shown',
    PRODUCT_CARD_CLICKED: 'product_card_clicked',
    VARIANT_SELECTED: 'variant_selected',
    COMPARE: 'comparison_viewed',
    ADD_TO_CART_START: 'add_to_cart_start',
    AVAILABILITY_VERIFIED: 'availability_verified',
    ADD_TO_CART: 'add_to_cart_from_chat',
    CART_UPDATED: 'cart_updated_from_chat',
    ADD_TO_CART_ERROR: 'add_to_cart_error',
    CART_VIEW: 'cart_view',
    CHECKOUT: 'checkout_started_from_chat',
    FEEDBACK_HELPFUL: 'answer_helpful',
    FEEDBACK_UNHELPFUL: 'answer_unhelpful',
    VERIFICATION_FAILED: 'verification_failed',
    HUMAN_HANDOFF: 'human_handoff',
    CLEAR_HISTORY: 'clear_history',
    SESSION_CLEAR_FAILED: 'session_clear_failed',
    ERROR: 'error'
  });

  const initialSuggestions = Array.from(suggestionsNode.querySelectorAll('[data-chat-prompt]'))
    .map((button) => String(button.dataset.chatPrompt || button.textContent || '').trim())
    .filter(Boolean);

  const config = {
    streamUrl: apiUrl(root.dataset.streamPath || '/message/stream'),
    messageUrl: apiUrl(root.dataset.messagePath || '/message'),
    availabilityUrl: apiUrl(root.dataset.availabilityPath || '/availability'),
    sessionClearUrl: apiUrl(root.dataset.sessionClearPath || '/session/clear'),
    eventUrl: apiUrl(root.dataset.eventPath || '/event'),
    cartAddUrl: root.dataset.cartAddUrl || window.routes?.cart_add_url || shopifyRoute('cart/add.js'),
    cartUrl: root.dataset.cartUrl || window.routes?.cart_url || shopifyRoute('cart'),
    cartJsonUrl: root.dataset.cartJsonUrl || shopifyRoute('cart.js'),
    cartUpdateUrl: root.dataset.cartUpdateUrl || window.routes?.cart_update_url || shopifyRoute('cart/update.js'),
    checkoutUrl: root.dataset.checkoutUrl || '/checkout',
    compareUrl: root.dataset.compareUrl || '/pages/compare',
    whatsappUrl: root.dataset.whatsappUrl || 'https://wa.me/27828901654',
    locale: root.dataset.locale || document.documentElement.lang || 'en-ZA',
    currency: root.dataset.currency || window.Shopify?.currency?.active || 'ZAR',
    maxQuantity: clampInteger(root.dataset.maxQuantity, 1, 999, 99),
    streamEnabled: dataBoolean(root.dataset.streamEnabled, true),
    availabilityEnabled: dataBoolean(root.dataset.availabilityEnabled, true)
  };

  const history = restoreHistory();
  const state = {
    sessionId: getSessionId(),
    sending: false,
    welcomed: false,
    streamCapability: config.streamEnabled ? null : false,
    availabilityCapability: config.availabilityEnabled ? null : false,
    activeController: null,
    lastFocused: null,
    lastAttributedProduct: null,
    cart: null
  };

  let productSequence = 0;
  let announceTimer;

  openButton.addEventListener('click', openChat);
  closeButton.addEventListener('click', closeChat);
  clearButton?.addEventListener('click', clearHistory);
  form.addEventListener('submit', handleSubmit);
  input.addEventListener('keydown', handleInputKeydown);
  input.addEventListener('input', resizeInput);
  suggestionsNode.addEventListener('click', handleSuggestionClick);
  root.addEventListener('click', handleRootClick);
  document.addEventListener('keydown', handleDialogKeydown);
  document.addEventListener('cart:updated', handleExternalCartUpdate);
  window.addEventListener('techticCompareUpdated', refreshCompareButtons);
  window.addEventListener('storage', (event) => {
    if (event.key === 'productDataCompare') refreshCompareButtons();
  });

  if (cartViewLink) cartViewLink.href = safeUrl(config.cartUrl, '/cart');
  if (checkoutLink) checkoutLink.href = safeUrl(config.checkoutUrl, '/checkout');
  updateClearButton();

  function handleSubmit(event) {
    event.preventDefault();
    sendMessage(input.value);
  }

  function handleInputKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      form.requestSubmit();
    }
  }

  function handleSuggestionClick(event) {
    const button = closestElement(event, '[data-chat-prompt]');
    if (button) sendMessage(button.dataset.chatPrompt || button.textContent || '');
  }

  function handleRootClick(event) {
    const handoff = closestElement(event, '[data-chat-handoff]');
    if (handoff) {
      track(EVENTS.HUMAN_HANDOFF, productFromDataset(handoff), {
        source: handoff.dataset.handoffSource || 'chat'
      });
      return;
    }

    const cartView = closestElement(event, '[data-chat-cart-view]');
    if (cartView) {
      track(EVENTS.CART_VIEW, state.lastAttributedProduct, {
        item_count: state.cart?.item_count || 0
      });
      return;
    }

    const checkout = closestElement(event, '[data-chat-checkout]');
    if (!checkout) return;
    if (checkout.getAttribute('aria-disabled') === 'true' || !state.cart?.item_count) {
      event.preventDefault();
      announce('Your cart is empty.');
      return;
    }

    track(EVENTS.CHECKOUT, state.lastAttributedProduct, {
      item_count: state.cart.item_count,
      cart_value: centsToAmount(state.cart.total_price)
    });
    markCartAssisted(state.lastAttributedProduct, 'checkout');
  }

  function handleExternalCartUpdate(event) {
    if (event.detail?.source === 'techtic-chat') return;
    refreshCartState();
  }

  function handleDialogKeydown(event) {
    if (panel.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeChat();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = getFocusableElements(panel);
    if (focusable.length === 0) {
      event.preventDefault();
      panel.focus({ preventScroll: true });
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openChat() {
    if (!panel.hidden) return;
    state.lastFocused = document.activeElement;
    panel.hidden = false;
    openButton.hidden = true;
    openButton.setAttribute('aria-expanded', 'true');
    root.classList.add('is-open');
    document.documentElement.classList.add('techtic-chat-open');

    if (!state.welcomed) {
      if (history.length === 0) {
        renderMessage('assistant', welcomeText());
      } else {
        history.forEach((message) => renderMessage(message.role, message.content));
      }
      state.welcomed = true;
    }

    refreshCartState();
    window.requestAnimationFrame(() => input.focus({ preventScroll: true }));
    track(EVENTS.OPEN);
  }

  function closeChat() {
    if (panel.hidden) return;
    panel.hidden = true;
    openButton.hidden = false;
    openButton.setAttribute('aria-expanded', 'false');
    root.classList.remove('is-open');
    document.documentElement.classList.remove('techtic-chat-open');
    const focusTarget = state.lastFocused instanceof HTMLElement && state.lastFocused.isConnected
      ? state.lastFocused
      : openButton;
    focusTarget.focus({ preventScroll: true });
  }

  function clearHistory() {
    if (state.sending) return;
    clearServerHistory();
    history.splice(0, history.length);
    saveHistory();
    messagesNode.replaceChildren();
    clearSuggestions();
    renderMessage('assistant', welcomeText());
    renderSuggestions(initialSuggestions);
    updateClearButton();
    input.value = '';
    resizeInput();
    input.focus({ preventScroll: true });
    announce('Chat history cleared.');
    track(EVENTS.CLEAR_HISTORY);
  }

  async function clearServerHistory() {
    try {
      const response = await fetch(config.sessionClearUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ sessionId: state.sessionId }),
        keepalive: true
      });
      if (!response.ok && ![404, 405, 501].includes(response.status)) {
        throw new Error('Session clear failed with HTTP ' + response.status);
      }
    } catch (error) {
      track(EVENTS.SESSION_CLEAR_FAILED, null, { reason: publicReason(error) });
    }
  }

  async function sendMessage(rawMessage) {
    const message = String(rawMessage || '').trim();
    if (!message || state.sending) return;
    if (panel.hidden) openChat();

    state.sending = true;
    input.value = '';
    resizeInput();
    setBusy(true);
    clearSuggestions();
    addMessage('user', message, true);

    const typing = renderMessage('assistant', text('thinking', 'Sam is checking...'), 'typing');
    typing.setAttribute('role', 'status');
    const requestBody = {
      sessionId: state.sessionId,
      requestId: createRequestId(),
      messages: history.slice(-10),
      context: {
        path: root.dataset.pagePath,
        pageType: root.dataset.pageType,
        productHandle: root.dataset.productHandle,
        productTitle: root.dataset.productTitle,
        productPrice: root.dataset.productPrice
      }
    };

    track(EVENTS.MESSAGE, null, { message_length: message.length });

    try {
      const result = await requestAssistant(requestBody, typing);
      finalizeAssistantResponse(typing, result, message);
    } catch (error) {
      typing.remove();
      const errorMessage = text('error', 'I could not verify that right now. Please try again or chat to our team on WhatsApp.');
      const errorNode = addMessage('assistant', errorMessage, true, 'error');
      renderRecoveryHandoff(errorNode, null, 'message_error');
      renderSuggestions(['Try again', 'Delivery information']);
      announce('Shopping help is temporarily unavailable.');
      track(EVENTS.ERROR, null, { reason: publicReason(error) });
    } finally {
      state.activeController = null;
      state.sending = false;
      setBusy(false);
      if (!panel.hidden) input.focus({ preventScroll: true });
    }
  }

  async function requestAssistant(requestBody, typing) {
    if (state.streamCapability !== false) {
      try {
        return await requestStream(requestBody, typing);
      } catch (error) {
        if (!(error instanceof StreamUnavailableError)) throw error;
        state.streamCapability = false;
        typing.textContent = text('thinking', 'Sam is checking...');
        typing.classList.add('techtic-chat__message--typing');
        typing.classList.remove('techtic-chat__message--streaming');
        track(EVENTS.STREAM_FALLBACK, null, { reason: publicReason(error) });
      }
    }
    return requestJson(requestBody);
  }

  async function requestStream(requestBody, typing) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 45000);
    state.activeController = controller;

    let response;
    try {
      response = await fetch(config.streamUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream'
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });
    } catch (error) {
      window.clearTimeout(timeout);
      throw new StreamUnavailableError(publicReason(error) || 'Stream request unavailable');
    }

    if ([404, 405, 406, 415, 500, 501, 502, 503, 504].includes(response.status)) {
      window.clearTimeout(timeout);
      throw new StreamUnavailableError('Streaming endpoint unavailable');
    }

    const contentType = String(response.headers.get('content-type') || '').toLowerCase();
    if (contentType.includes('application/json')) {
      try {
        const payload = await readJson(response);
        if (!response.ok || payload?.ok === false) {
          throw new Error(payload?.error || 'Streaming request failed');
        }
        state.streamCapability = false;
        return normalizeAssistantResponse(payload, '', 'stream-json');
      } finally {
        window.clearTimeout(timeout);
      }
    }

    if (!response.ok) {
      window.clearTimeout(timeout);
      throw new Error('Streaming request failed with HTTP ' + response.status);
    }
    if (!response.body || !contentType.includes('text/event-stream')) {
      window.clearTimeout(timeout);
      throw new StreamUnavailableError('Streaming response was not SSE');
    }

    state.streamCapability = true;
    track(EVENTS.STREAM_STARTED);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let eventName = 'message';
    let dataLines = [];
    let receivedEvent = false;
    let streamedText = '';
    let finalPayload;
    let terminalError;

    const dispatchEvent = () => {
      if (dataLines.length === 0) {
        eventName = 'message';
        return;
      }

      receivedEvent = true;
      const rawData = dataLines.join('\n');
      dataLines = [];
      const parsed = parseEventData(rawData);
      const typeName = sseEventType(eventName, parsed);
      eventName = 'message';

      if (typeName === 'status') {
        const status = eventValue(parsed, ['status', 'message', 'text', 'label']);
        if (status) {
          typing.textContent = status;
          announce(status);
        }
        return;
      }

      if (typeName === 'token') {
        const token = eventValue(parsed, ['token', 'delta', 'text', 'content']);
        if (!token) return;
        streamedText += token;
        typing.textContent = streamedText;
        typing.classList.remove('techtic-chat__message--typing');
        typing.classList.add('techtic-chat__message--streaming');
        typing.removeAttribute('role');
        scrollToLatest();
        return;
      }

      if (typeName === 'final' || rawData === '[DONE]') {
        finalPayload = rawData === '[DONE]'
          ? { reply: streamedText }
          : unwrapSsePayload(parsed, 'final');
        return;
      }

      if (typeName === 'error') {
        const errorMessage = eventValue(parsed, ['error', 'message', 'text']) || 'Streaming response failed';
        terminalError = new Error(errorMessage);
      }
    };

    const processLine = (rawLine) => {
      const line = rawLine.replace(/^\uFEFF/, '');
      if (line === '') {
        dispatchEvent();
      } else if (line.startsWith('event:')) {
        eventName = line.slice(6).trim() || 'message';
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).replace(/^ /, ''));
      }
    };

    try {
      while (!finalPayload && !terminalError) {
        const chunk = await reader.read();
        if (chunk.done) break;
        buffer += decoder.decode(chunk.value, { stream: true });

        let lineBreak = findLineBreak(buffer);
        while (lineBreak) {
          processLine(buffer.slice(0, lineBreak.index));
          buffer = buffer.slice(lineBreak.index + lineBreak.length);
          if (finalPayload || terminalError) break;
          lineBreak = findLineBreak(buffer);
        }
      }

      buffer += decoder.decode();
      if (buffer) processLine(buffer);
      dispatchEvent();
      if (terminalError) throw terminalError;
      if (finalPayload) {
        reader.cancel().catch(() => {});
        return normalizeAssistantResponse(finalPayload, streamedText, 'stream');
      }
      if (streamedText) {
        return normalizeAssistantResponse({ reply: streamedText }, streamedText, 'stream');
      }
      if (!receivedEvent) throw new StreamUnavailableError('No SSE events received');
      throw new Error('The streaming response ended before a final event');
    } catch (error) {
      if (!receivedEvent && !(error instanceof StreamUnavailableError)) {
        throw new StreamUnavailableError(publicReason(error));
      }
      throw error;
    } finally {
      window.clearTimeout(timeout);
      reader.releaseLock();
    }
  }

  async function requestJson(requestBody) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 30000);
    state.activeController = controller;
    try {
      const response = await fetch(config.messageUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });
      const payload = await readJson(response);
      if (!response.ok || payload?.ok === false) {
        throw new Error(payload?.error || 'Message request failed with HTTP ' + response.status);
      }
      return normalizeAssistantResponse(payload, '', 'json');
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function normalizeAssistantResponse(rawPayload, streamedText, transport) {
    const payload = rawPayload && typeof rawPayload === 'object' ? rawPayload : {};
    const candidates = compactObjects([
      payload,
      payload.data,
      payload.payload,
      payload.response,
      payload.result,
      payload.data?.response,
      payload.payload?.response
    ]);

    const reply = firstString(
      ...candidates.map((item) => item.reply),
      ...candidates.map((item) => item.answer),
      ...candidates.map((item) => typeof item.message === 'string' ? item.message : item.message?.content),
      streamedText
    );
    const products = firstArrayFrom(candidates, ['products', 'recommendations', 'matches']);
    const actions = firstArrayFrom(candidates, ['actions', 'clientActions']);
    const suggestedReplies = firstArrayFrom(candidates, ['suggestedReplies', 'suggestions', 'quickReplies'])
      .map((value) => typeof value === 'string' ? value.trim() : '')
      .filter(Boolean);

    return {
      reply,
      products,
      actions,
      suggestedReplies,
      responseId: firstString(
        ...candidates.map((item) => item.responseId),
        ...candidates.map((item) => item.messageId),
        ...candidates.map((item) => item.requestId)
      ),
      verifiedAt: firstString(
        ...candidates.map((item) => item.verifiedAt),
        ...candidates.map((item) => item.availabilityVerifiedAt),
        ...candidates.map((item) => item.checkedAt)
      ),
      source: firstString(...candidates.map((item) => item.source)),
      transport
    };
  }

  function finalizeAssistantResponse(typing, result, customerMessage) {
    const reply = String(result.reply || '').trim()
      || (result.products.length ? 'Here are the closest verified matches I found.' : 'I could not find a verified match for that request.');

    typing.className = 'techtic-chat__message techtic-chat__message--assistant';
    typing.textContent = reply;
    typing.removeAttribute('role');
    typing.removeAttribute('aria-label');
    persistMessage('assistant', reply);

    const productCount = shouldRenderProducts(customerMessage, result)
      ? renderProducts(result.products, result.verifiedAt)
      : 0;
    void processClientActions(result.actions, result.products, result.verifiedAt);
    renderFeedback(result.responseId);
    renderSuggestions(result.suggestedReplies);
    scrollToLatest();
    announce('Response ready.');
    track(EVENTS.RESPONSE, null, {
      product_count: productCount,
      source: result.source,
      transport: result.transport,
      response_id: result.responseId
    });
  }

  function shouldRenderProducts(customerMessage, result) {
    if (!Array.isArray(result.products) || result.products.length === 0) return false;
    if (Array.isArray(result.actions) && result.actions.some((action) => (
      action?.type === 'view_product' || action?.type === 'add_to_cart'
    ))) return true;
    return /\b(?:sell|stock|available|have|carry|looking for|find|search|show|recommend|suggest|choose|buy|price|cost|budget|under|cheapest|best|product|item|model|sku|phone|tablet|laptop|computer|monitor|printer|router|wifi|network|ssd|storage|memory|ram|charger|cable|adapter|dock|keyboard|mouse|headphone|speaker|camera|cctv|ups|inverter|projector|watch|gaming|appliance)\b/i.test(String(customerMessage || ''));
  }

  function addMessage(role, content, persist, modifier) {
    const clean = String(content || '').trim();
    if (!clean) return null;
    const message = renderMessage(role, clean, modifier);
    if (persist) persistMessage(role, clean);
    return message;
  }

  function persistMessage(role, content) {
    history.push({ role, content });
    while (history.length > 10) history.shift();
    saveHistory();
    updateClearButton();
  }

  function renderMessage(role, content, modifier) {
    const message = element('div', 'techtic-chat__message techtic-chat__message--' + role, content);
    if (modifier) message.classList.add('techtic-chat__message--' + modifier);
    messagesNode.appendChild(message);
    scrollToLatest();
    return message;
  }

  function renderFeedback(responseId) {
    const feedback = element('div', 'techtic-chat__feedback');
    const prompt = element('span', 'techtic-chat__feedback-label', 'Was this helpful?');
    const helpful = button('Helpful', 'techtic-chat__feedback-button');
    const unhelpful = button('Not helpful', 'techtic-chat__feedback-button');
    const status = element('span', 'techtic-chat__feedback-status');
    helpful.setAttribute('aria-pressed', 'false');
    unhelpful.setAttribute('aria-pressed', 'false');
    status.setAttribute('role', 'status');

    const submitFeedback = (isHelpful) => {
      if (helpful.disabled) return;
      helpful.disabled = true;
      unhelpful.disabled = true;
      helpful.setAttribute('aria-pressed', String(isHelpful));
      unhelpful.setAttribute('aria-pressed', String(!isHelpful));
      status.textContent = 'Thank you';
      track(isHelpful ? EVENTS.FEEDBACK_HELPFUL : EVENTS.FEEDBACK_UNHELPFUL, null, {
        response_id: responseId
      });
      if (!isHelpful) {
        const handoff = createHandoffLink(null, 'feedback');
        handoff.textContent = 'Chat to a person';
        feedback.appendChild(handoff);
      }
      announce('Feedback recorded. Thank you.');
    };

    helpful.addEventListener('click', () => submitFeedback(true));
    unhelpful.addEventListener('click', () => submitFeedback(false));
    feedback.append(prompt, helpful, unhelpful, status);
    messagesNode.appendChild(feedback);
  }

  function renderProducts(rawProducts, responseVerifiedAt) {
    const products = Array.isArray(rawProducts)
      ? rawProducts.slice(0, 4).map((product) => normalizeProduct(product, responseVerifiedAt)).filter(Boolean)
      : [];
    if (products.length === 0) return 0;

    const list = element('div', 'techtic-chat__products');
    list.setAttribute('role', 'list');
    list.setAttribute('aria-label', 'Recommended products');
    products.forEach((product, index) => {
      list.appendChild(createProductCard(product, index));
      track(EVENTS.RECOMMENDATION_IMPRESSION, product, { position: index + 1 });
    });
    messagesNode.appendChild(list);
    scrollToLatest();
    return products.length;
  }

  async function processClientActions(rawActions, rawProducts, responseVerifiedAt) {
    const actions = Array.isArray(rawActions) ? rawActions : [];
    const products = Array.isArray(rawProducts)
      ? rawProducts.map((product) => normalizeProduct(product, responseVerifiedAt)).filter(Boolean)
      : [];
    for (const action of actions) {
      if (!action || action.type !== 'add_to_cart') continue;
      const variantId = shopifyId(firstString(action.variantId, action.variant_id));
      const quantity = clampInteger(action.quantity, 1, config.maxQuantity, 1);
      const product = products.find((candidate) => candidate.variants.some((variant) => variant.id === variantId));
      const variant = product?.variants.find((candidate) => candidate.id === variantId);
      if (!product || !variant || !variantId) {
        addMessage('assistant', 'I could not confirm the exact item for the cart. Please use the Add button on the product card.', true, 'error');
        continue;
      }

      const status = renderMessage('assistant', 'Adding the verified item to your cart...', 'typing');
      status.setAttribute('role', 'status');
      try {
        const verification = await revalidateAvailability(product, variant, quantity);
        if (!verification.supported || verification.canFulfill === false || variant.available === false) {
          throw new VerificationError('The exact item and quantity could not be verified for your cart.');
        }
        await addShopifyCartItem(variantId, quantity, sendButton);
        markCartAssisted(product, 'add_to_cart', variantId);
        track(EVENTS.ADD_TO_CART, product, { variant_id: variantId, quantity, source: 'direct_chat_request' });
        status.className = 'techtic-chat__message techtic-chat__message--assistant techtic-chat__message--success';
        status.textContent = quantity + ' ' + pluralize(quantity, 'item', 'items') + ' added to your cart.';
        await refreshCartState();
        announce('Item added to cart.');
      } catch (error) {
        status.className = 'techtic-chat__message techtic-chat__message--assistant techtic-chat__message--error';
        status.textContent = error instanceof VerificationError
          ? error.message
          : 'The cart could not be updated. Please use the Add button on the product card.';
        track(EVENTS.ADD_TO_CART_ERROR, product, { variant_id: variantId, quantity, reason: publicReason(error) });
        renderRecoveryHandoff(status, product, 'direct_cart_error');
      }
      scrollToLatest();
    }
  }

  function normalizeProduct(rawProduct, responseVerifiedAt) {
    if (!rawProduct || typeof rawProduct !== 'object') return null;
    const raw = rawProduct.product && typeof rawProduct.product === 'object'
      ? { ...rawProduct.product, ...rawProduct }
      : rawProduct;

    const url = safeUrl(firstString(raw.url, raw.productUrl, raw.onlineStoreUrl), '');
    const handle = firstString(raw.handle, handleFromUrl(url));
    const title = firstString(raw.title, raw.name, 'Product');
    const productPrice = moneyValue(raw, 'price');
    const productCompareAtPrice = moneyValue(raw, 'compareAtPrice');
    const productInventory = firstNumber(raw.inventoryQuantity, raw.availableQuantity, raw.quantityAvailable, raw.stock);
    const productAvailable = firstBoolean(raw.available, raw.inStock, raw.isAvailable);
    const productVerifiedAt = firstString(
      raw.verifiedAt,
      raw.availabilityVerifiedAt,
      raw.checkedAt,
      responseVerifiedAt,
      new Date().toISOString()
    );
    const image = normalizeImage(raw);
    const rawVariants = arrayValue(raw.variants);
    const defaults = {
      price: productPrice,
      compareAtPrice: productCompareAtPrice,
      inventoryQuantity: productInventory,
      available: productAvailable,
      verifiedAt: productVerifiedAt,
      imageUrl: image.url,
      imageAlt: image.alt
    };

    let variants = rawVariants.map((variant, index) => normalizeVariant(variant, defaults, index, rawVariants.length));
    if (variants.length === 0) {
      variants = [normalizeVariant({
        id: firstString(raw.variantId, raw.selectedVariantId, raw.variant?.id),
        title: firstString(raw.variantTitle, raw.variant?.title, 'Standard'),
        price: productPrice,
        compareAtPrice: productCompareAtPrice,
        inventoryQuantity: productInventory,
        available: productAvailable,
        verifiedAt: productVerifiedAt,
        imageUrl: image.url,
        imageAlt: image.alt,
        availability: raw.availability
      }, defaults, 0, 1)];
    }

    const preferredVariantId = shopifyId(firstString(raw.selectedVariantId, raw.variantId, raw.variant?.id));
    const selectedIndex = Math.max(0, variants.findIndex((variant) => variant.id === preferredVariantId));

    return {
      productId: String(firstString(raw.productId, raw.id, raw.shopifyProductId, '') || ''),
      handle,
      title,
      url: url || (handle ? '/products/' + encodeURIComponent(handle) : ''),
      imageUrl: image.url,
      imageAlt: firstString(image.alt, title),
      price: productPrice,
      compareAtPrice: productCompareAtPrice,
      matchReason: firstString(raw.matchReason, raw.reason, raw.whyRecommended, raw.match?.reason),
      verifiedAt: productVerifiedAt,
      variants,
      selectedIndex: selectedIndex >= 0 ? selectedIndex : 0
    };
  }

  function normalizeVariant(rawVariant, defaults, index, count) {
    const raw = rawVariant && typeof rawVariant === 'object' ? rawVariant : {};
    const selectedOptions = arrayValue(raw.selectedOptions)
      .map((option) => {
        if (typeof option === 'string') return option;
        if (!option || typeof option !== 'object') return '';
        const name = firstString(option.name, option.optionName);
        const value = firstString(option.value, option.optionValue);
        return name && value ? name + ': ' + value : value;
      })
      .filter(Boolean);
    const optionValues = arrayValue(raw.options || raw.optionValues)
      .map((option) => typeof option === 'string' ? option : firstString(option?.value, option?.name))
      .filter(Boolean);
    let title = firstString(raw.displayName, raw.title, raw.name, raw.optionTitle, ...selectedOptions, ...optionValues);
    if (!title || /^default title$/i.test(title)) title = count > 1 ? 'Option ' + (index + 1) : 'Standard';

    const inventory = firstNumber(
      raw.inventoryQuantity,
      raw.availableQuantity,
      raw.quantityAvailable,
      raw.inventory,
      raw.stock,
      count === 1 ? defaults.inventoryQuantity : null
    );
    let available = firstBoolean(raw.available, raw.inStock, raw.isAvailable, count === 1 ? defaults.available : null);
    if (inventory !== null) available = inventory > 0;

    const image = normalizeImage(raw);
    return {
      id: shopifyId(firstString(raw.variantId, raw.id, raw.shopifyVariantId)),
      title,
      price: moneyValue(raw, 'price') ?? defaults.price,
      compareAtPrice: moneyValue(raw, 'compareAtPrice') ?? defaults.compareAtPrice,
      inventoryQuantity: inventory === null ? null : Math.max(0, Math.floor(inventory)),
      available,
      availabilityText: firstString(raw.availabilityText, typeof raw.availability === 'string' ? raw.availability : ''),
      verifiedAt: firstString(raw.verifiedAt, raw.availabilityVerifiedAt, raw.checkedAt, defaults.verifiedAt),
      imageUrl: image.url || defaults.imageUrl,
      imageAlt: image.alt || defaults.imageAlt
    };
  }

  function createProductCard(product, position) {
    const card = element('article', 'techtic-chat__product');
    card.setAttribute('role', 'listitem');
    card.dataset.productPosition = String(position + 1);

    const summary = element('div', 'techtic-chat__product-summary');
    const imageLink = element('a', 'techtic-chat__product-image');
    const image = document.createElement('img');
    image.loading = 'lazy';
    image.width = 160;
    image.height = 160;
    image.alt = product.imageAlt || product.title;
    if (product.imageUrl) {
      image.src = safeImageUrl(product.imageUrl);
      imageLink.appendChild(image);
    } else {
      imageLink.appendChild(element('span', 'techtic-chat__image-placeholder', 'Image unavailable'));
    }

    const headline = element('div', 'techtic-chat__product-headline');
    const titleLink = element('a', 'techtic-chat__product-title', product.title);
    const price = element('div', 'techtic-chat__price');
    const currentPrice = element('strong');
    const previousPrice = element('s');
    const saving = element('span', 'techtic-chat__saving');
    price.append(currentPrice, previousPrice, saving);
    headline.append(titleLink, price);
    summary.append(imageLink, headline);

    const matchReason = element(
      'p',
      'techtic-chat__match',
      product.matchReason || 'Recommended from the details in your request.'
    );
    const facts = element('div', 'techtic-chat__facts');
    const stock = element('span', 'techtic-chat__stock');
    const verified = document.createElement('time');
    verified.className = 'techtic-chat__verified';
    facts.append(stock, verified);

    const variantBlock = element('div', 'techtic-chat__variant');
    const variantLabel = element('label', '', product.variants.length > 1 ? 'Choose variant' : 'Variant');
    const variantId = 'techtic-chat-variant-' + (++productSequence);
    variantLabel.htmlFor = variantId;
    let variantControl;
    if (product.variants.length > 1) {
      variantControl = document.createElement('select');
      variantControl.id = variantId;
      variantControl.setAttribute('aria-label', 'Choose variant for ' + product.title);
      product.variants.forEach((variant, index) => {
        const option = document.createElement('option');
        option.value = String(index);
        option.textContent = variantOptionLabel(variant);
        variantControl.appendChild(option);
      });
      variantControl.value = String(product.selectedIndex);
    } else {
      variantControl = element('strong', 'techtic-chat__variant-value', product.variants[0].title);
      variantControl.id = variantId;
    }
    variantBlock.append(variantLabel, variantControl);

    const purchase = element('div', 'techtic-chat__purchase');
    const quantity = element('div', 'techtic-chat__quantity');
    quantity.setAttribute('role', 'group');
    quantity.setAttribute('aria-label', 'Quantity for ' + product.title);
    const minus = button('-', 'techtic-chat__quantity-button');
    minus.setAttribute('aria-label', 'Decrease quantity');
    const quantityInput = document.createElement('input');
    quantityInput.type = 'number';
    quantityInput.inputMode = 'numeric';
    quantityInput.min = '1';
    quantityInput.step = '1';
    quantityInput.value = '1';
    quantityInput.setAttribute('aria-label', 'Quantity');
    const plus = button('+', 'techtic-chat__quantity-button');
    plus.setAttribute('aria-label', 'Increase quantity');
    quantity.append(minus, quantityInput, plus);

    const addButton = button(text('add', 'Add to cart'), 'techtic-chat__add');
    purchase.append(quantity, addButton);

    const actions = element('div', 'techtic-chat__product-actions');
    const viewLink = element('a', '', text('viewProduct', 'View'));
    const compareButton = button('Compare', 'techtic-chat__compare');
    compareButton.setAttribute('aria-pressed', 'false');
    actions.append(viewLink, compareButton);

    const status = element('div', 'techtic-chat__product-status');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.hidden = true;

    card.append(summary, matchReason, facts, variantBlock, purchase, actions, status);

    let selectedIndex = product.selectedIndex;
    const selectedVariant = () => product.variants[selectedIndex] || product.variants[0];

    const updateCard = (keepStatus) => {
      const variant = selectedVariant();
      const availableQuantity = variant.inventoryQuantity;
      const knownUnavailable = variant.available === false || availableQuantity === 0;
      const maxQuantity = availableQuantity === null
        ? config.maxQuantity
        : Math.max(1, Math.min(config.maxQuantity, availableQuantity));
      const cleanQuantity = clampInteger(quantityInput.value, 1, maxQuantity, 1);
      quantityInput.value = String(cleanQuantity);
      quantityInput.max = String(maxQuantity);
      quantityInput.disabled = knownUnavailable;
      minus.disabled = knownUnavailable || cleanQuantity <= 1;
      plus.disabled = knownUnavailable || cleanQuantity >= maxQuantity;

      currentPrice.textContent = formatMoney(variant.price ?? product.price);
      const hasSaving = variant.compareAtPrice !== null
        && variant.price !== null
        && variant.compareAtPrice > variant.price;
      previousPrice.hidden = !hasSaving;
      saving.hidden = !hasSaving;
      previousPrice.textContent = hasSaving ? formatMoney(variant.compareAtPrice) : '';
      saving.textContent = hasSaving ? 'Save ' + formatMoney(variant.compareAtPrice - variant.price) : '';

      stock.textContent = availabilityLabel(variant);
      stock.dataset.tone = knownUnavailable ? 'unavailable' : variant.inventoryQuantity === null ? 'pending' : 'available';
      const verifiedDate = validDate(variant.verifiedAt || product.verifiedAt) || new Date();
      verified.dateTime = verifiedDate.toISOString();
      verified.textContent = verifiedLabel(verifiedDate);

      const productUrl = withVariant(product.url, variant.id);
      imageLink.href = productUrl || '#';
      titleLink.href = productUrl || '#';
      viewLink.href = productUrl || '#';
      if (variant.imageUrl) {
        image.src = safeImageUrl(variant.imageUrl);
        image.alt = variant.imageAlt || product.imageAlt || product.title;
      }

      addButton.disabled = knownUnavailable || !variant.id;
      addButton.textContent = knownUnavailable ? 'Unavailable' : text('add', 'Add to cart');
      compareButton.dataset.compareId = variant.id || product.productId;
      compareButton.dataset.productHandle = product.handle || '';
      compareButton.dataset.productTitle = product.title;
      compareButton.dataset.productId = product.productId;
      compareButton.dataset.productPrice = String(variant.price ?? product.price ?? '');
      refreshCompareButtons();

      if (variantControl instanceof HTMLSelectElement) {
        Array.from(variantControl.options).forEach((option, index) => {
          option.textContent = variantOptionLabel(product.variants[index]);
        });
      } else {
        variantControl.textContent = variant.title;
      }

      if (!keepStatus) {
        status.hidden = true;
        status.replaceChildren();
        delete status.dataset.tone;
      }
    };

    if (variantControl instanceof HTMLSelectElement) {
      variantControl.addEventListener('change', () => {
        selectedIndex = clampInteger(variantControl.value, 0, product.variants.length - 1, 0);
        product.selectedIndex = selectedIndex;
        updateCard(false);
        track(EVENTS.VARIANT_SELECTED, product, {
          variant_id: selectedVariant().id,
          position: position + 1
        });
      });
    }

    minus.addEventListener('click', () => {
      quantityInput.value = String(Math.max(1, Number(quantityInput.value || 1) - 1));
      updateCard(true);
    });
    plus.addEventListener('click', () => {
      quantityInput.value = String(Number(quantityInput.value || 1) + 1);
      updateCard(true);
    });
    quantityInput.addEventListener('change', () => updateCard(true));
    quantityInput.addEventListener('blur', () => updateCard(true));

    [imageLink, titleLink, viewLink].forEach((link) => {
      link.addEventListener('click', (event) => {
        if (!product.url) event.preventDefault();
        recordRecommendationClick(product, selectedVariant(), 'view', position + 1);
      });
    });

    compareButton.addEventListener('click', () => {
      toggleCompare(product, selectedVariant(), compareButton, status, position + 1);
    });
    addButton.addEventListener('click', () => {
      addToCart(product, selectedVariant(), Number(quantityInput.value), addButton, status, updateCard, position + 1);
    });

    updateCard(false);
    return card;
  }

  async function addToCart(product, variant, requestedQuantity, addButton, status, updateCard, position) {
    if (!variant.id || addButton.disabled) return;
    const quantity = clampInteger(requestedQuantity, 1, config.maxQuantity, 1);
    addButton.disabled = true;
    addButton.textContent = 'Checking...';
    setProductStatus(status, 'Rechecking price and exact availability...', 'checking');
    track(EVENTS.ADD_TO_CART_START, product, {
      variant_id: variant.id,
      quantity,
      position
    });

    try {
      const verification = await revalidateAvailability(product, variant, quantity);
      if (verification.supported) {
        track(EVENTS.AVAILABILITY_VERIFIED, product, {
          variant_id: variant.id,
          quantity,
          available_quantity: variant.inventoryQuantity
        });
      } else if (variant.inventoryQuantity === null && variant.available !== true) {
        throw new VerificationError('Live availability is not available for this variant.');
      }

      if (variant.available === false || variant.inventoryQuantity === 0) {
        throw new VerificationError('This variant is currently unavailable.');
      }
      if (variant.inventoryQuantity !== null && quantity > variant.inventoryQuantity) {
        throw new VerificationError(
          'Only ' + variant.inventoryQuantity + ' ' + pluralize(variant.inventoryQuantity, 'item', 'items') + ' available.'
        );
      }

      updateCard(true);
      addButton.disabled = true;
      addButton.textContent = 'Adding...';
      await addShopifyCartItem(variant.id, quantity, addButton);
      state.lastAttributedProduct = product;
      markCartAssisted(product, 'add_to_cart', variant.id);
      track(EVENTS.ADD_TO_CART, product, {
        variant_id: variant.id,
        quantity,
        position
      });
      const updatedCart = await refreshCartState();
      track(EVENTS.CART_UPDATED, product, {
        variant_id: variant.id,
        quantity,
        item_count: updatedCart?.item_count ?? state.cart?.item_count ?? 0,
        cart_value: centsToAmount(updatedCart?.total_price ?? state.cart?.total_price)
      });
      setProductStatus(status, quantity + ' ' + pluralize(quantity, 'item', 'items') + ' added to your cart.', 'success');
      addButton.textContent = text('added', 'Added');
      announce('Added to cart.');
      window.setTimeout(() => {
        if (addButton.isConnected) updateCard(true);
      }, 2200);
    } catch (error) {
      const verificationFailed = error instanceof VerificationError;
      setProductStatus(
        status,
        verificationFailed
          ? error.message
          : publicReason(error) || 'This item could not be added. Please try again.',
        'error',
        verificationFailed ? product : null
      );
      track(verificationFailed ? EVENTS.VERIFICATION_FAILED : EVENTS.ADD_TO_CART_ERROR, product, {
        variant_id: variant.id,
        quantity,
        reason: publicReason(error)
      });
      announce(verificationFailed ? 'Availability verification failed.' : 'Could not add this item.');
      updateCard(true);
    }
  }

  async function revalidateAvailability(product, variant, quantity) {
    if (state.availabilityCapability === false) return { supported: false };

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 9000);
    let response;
    try {
      response = await fetch(config.availabilityUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          sessionId: state.sessionId,
          productId: product.productId || undefined,
          productHandle: product.handle || undefined,
          variantId: variant.id,
          quantity,
          context: {
            path: root.dataset.pagePath,
            pageType: root.dataset.pageType
          }
        }),
        signal: controller.signal
      });
    } catch (error) {
      throw new VerificationError(
        error?.name === 'AbortError'
          ? 'The live availability check timed out.'
          : 'Live availability could not be verified.'
      );
    } finally {
      window.clearTimeout(timeout);
    }

    if ([404, 405, 501].includes(response.status)) {
      state.availabilityCapability = false;
      return { supported: false };
    }

    const payload = await readJson(response);
    const availability = normalizeAvailability(payload, variant.id);
    if (!response.ok && !availability) {
      throw new VerificationError(firstString(payload?.error, payload?.message, 'Live availability could not be verified.'));
    }
    if (!availability) {
      throw new VerificationError('The live availability check returned no verifiable result.');
    }

    state.availabilityCapability = true;
    if (availability.variantId && availability.variantId !== variant.id) {
      throw new VerificationError('The selected variant could not be verified.');
    }
    if (availability.inventoryQuantity !== null) {
      variant.inventoryQuantity = availability.inventoryQuantity;
      variant.available = availability.inventoryQuantity > 0;
    } else if (availability.available !== null) {
      variant.available = availability.available;
    }
    if (availability.price !== null) variant.price = availability.price;
    if (availability.compareAtPrice !== null) variant.compareAtPrice = availability.compareAtPrice;
    variant.verifiedAt = availability.verifiedAt || new Date().toISOString();

    if (availability.canFulfill === false || variant.available === false) {
      throw new VerificationError(
        variant.inventoryQuantity === null
          ? 'This variant is currently unavailable.'
          : 'Only ' + variant.inventoryQuantity + ' ' + pluralize(variant.inventoryQuantity, 'item', 'items') + ' available.'
      );
    }
    if (variant.inventoryQuantity !== null && quantity > variant.inventoryQuantity) {
      throw new VerificationError(
        'Only ' + variant.inventoryQuantity + ' ' + pluralize(variant.inventoryQuantity, 'item', 'items') + ' available.'
      );
    }
    return { supported: true };
  }

  function normalizeAvailability(payload, expectedVariantId) {
    if (!payload || typeof payload !== 'object') return null;
    const product = payload.product || payload.data?.product;
    const productVariants = arrayValue(product?.variants);
    const matchingVariant = productVariants.find((item) => {
      const id = shopifyId(firstString(item?.variantId, item?.id));
      return id && id === expectedVariantId;
    });
    const candidates = compactObjects([
      payload.availability,
      payload.data?.availability,
      payload.variant,
      payload.data?.variant,
      matchingVariant,
      payload.data,
      payload
    ]);
    const candidate = candidates.find((item) => {
      return firstNumber(item.inventoryQuantity, item.availableQuantity, item.quantityAvailable, item.quantity, item.stock) !== null
        || firstBoolean(item.available, item.inStock, item.canFulfill) !== null
        || firstString(item.verifiedAt, item.checkedAt);
    });
    if (!candidate) return null;

    const inventoryQuantity = firstNumber(
      candidate.inventoryQuantity,
      candidate.availableQuantity,
      candidate.quantityAvailable,
      candidate.quantity,
      candidate.stock
    );
    return {
      variantId: shopifyId(firstString(candidate.variantId, candidate.id, expectedVariantId)),
      inventoryQuantity: inventoryQuantity === null ? null : Math.max(0, Math.floor(inventoryQuantity)),
      available: firstBoolean(candidate.available, candidate.inStock, candidate.isAvailable),
      canFulfill: firstBoolean(candidate.canFulfill, candidate.canFulfil, candidate.requestedQuantityAvailable),
      price: moneyValue(candidate, 'price'),
      compareAtPrice: moneyValue(candidate, 'compareAtPrice'),
      verifiedAt: firstString(candidate.verifiedAt, candidate.availabilityVerifiedAt, candidate.checkedAt)
    };
  }

  async function addShopifyCartItem(variantId, quantity, activeElement) {
    const cartSurface = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', String(quantity));

    if (cartSurface && typeof cartSurface.getSectionsToRender === 'function') {
      const sectionIds = cartSurface.getSectionsToRender()
        .map((section) => section?.id)
        .filter(Boolean);
      if (sectionIds.length) {
        formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
      }
      if (typeof cartSurface.setActiveElement === 'function') {
        cartSurface.setActiveElement(activeElement);
      }
    }

    const response = await fetch(config.cartAddUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    });
    const payload = await readJson(response);
    if (!response.ok || payload?.status) {
      throw new Error(firstString(payload?.description, payload?.message, 'Cart request failed.'));
    }

    try {
      cartSurface?.classList.remove('is-empty');
      if (cartSurface && typeof cartSurface.renderContents === 'function') {
        cartSurface.renderContents(payload);
      }
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'techtic-chat',
          productVariantId: variantId,
          cartData: payload
        });
      }
    } catch {
      // The item is already in Shopify's cart; theme rendering is enhancement-only.
    }

    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: {
        source: 'techtic-chat',
        variantId,
        quantity,
        cartData: payload
      }
    }));
    return payload;
  }

  async function refreshCartState() {
    if (!cartNode || !cartCountNode || !cartTotalNode || !checkoutLink) return null;
    try {
      const response = await fetch(config.cartJsonUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Cart request failed');
      const cart = await response.json();
      state.cart = cart;
      renderCartState(cart);
      return cart;
    } catch {
      if (!state.cart) {
        cartCountNode.textContent = 'Cart status unavailable';
        cartTotalNode.textContent = '';
        checkoutLink.setAttribute('aria-disabled', 'true');
      }
      return null;
    }
  }

  function renderCartState(cart) {
    const count = Math.max(0, Number(cart?.item_count || 0));
    cartCountNode.textContent = count === 0
      ? 'Cart empty'
      : count + ' ' + pluralize(count, 'item', 'items') + ' in cart';
    cartTotalNode.textContent = count ? formatCartMoney(cart.total_price) : '';
    cartNode.classList.toggle('has-items', count > 0);
    checkoutLink.setAttribute('aria-disabled', String(count === 0));
    checkoutLink.tabIndex = count === 0 ? -1 : 0;
  }

  function toggleCompare(product, variant, compareButton, status, position) {
    const id = variant.id || product.productId;
    if (!id || !product.handle) {
      setProductStatus(status, 'This product cannot be compared right now.', 'error');
      return;
    }

    try {
      const items = readCompareItems();
      const existingIndex = items.findIndex((item) => item.id === id);
      let action;
      if (existingIndex >= 0) {
        items.splice(existingIndex, 1);
        action = 'removed';
      } else {
        const limit = Number(window.TechTicCompare?.maxItems || 4);
        if (items.length >= limit) {
          setProductStatus(status, 'Compare is limited to ' + limit + ' products. Remove one first.', 'error');
          return;
        }
        items.push({ id, handle: product.handle });
        action = 'added';
      }

      writeCompareItems(items, compareButton);
      refreshCompareButtons();
      setProductStatus(
        status,
        action === 'added' ? 'Added to compare.' : 'Removed from compare.',
        'success'
      );
      recordRecommendationClick(product, variant, 'compare', position);
      track(EVENTS.COMPARE, product, {
        variant_id: variant.id,
        action,
        position
      });
    } catch {
      setProductStatus(status, 'Compare is unavailable in this browser.', 'error');
    }
  }

  function readCompareItems() {
    if (window.TechTicCompare && typeof window.TechTicCompare.readItems === 'function') {
      return window.TechTicCompare.readItems().map(normalizeCompareItem).filter(Boolean);
    }
    let raw = {};
    try {
      raw = JSON.parse(localStorage.getItem('productDataCompare') || '{}');
    } catch {
      raw = {};
    }
    if (Array.isArray(raw)) return raw.map(normalizeCompareItem).filter(Boolean);
    return Object.entries(raw || {}).map(([id, value]) => {
      return normalizeCompareItem(typeof value === 'string' ? { id, handle: value } : { id, ...value });
    }).filter(Boolean);
  }

  function writeCompareItems(items, target) {
    if (window.TechTicCompare && typeof window.TechTicCompare.writeItems === 'function') {
      window.TechTicCompare.writeItems(items, target);
      return;
    }
    const value = {};
    items.forEach((item) => {
      value[item.id] = item.handle;
    });
    if (Object.keys(value).length) localStorage.setItem('productDataCompare', JSON.stringify(value));
    else localStorage.removeItem('productDataCompare');
    window.dispatchEvent(new CustomEvent('techticCompareUpdated', { detail: { items, target } }));
  }

  function normalizeCompareItem(item) {
    if (!item || typeof item !== 'object') return null;
    const id = shopifyId(firstString(item.id, item.product_id));
    const handle = firstString(item.handle, item.value);
    return id && handle ? { id, handle } : null;
  }

  function refreshCompareButtons() {
    const selectedIds = new Set(readCompareItems().map((item) => item.id));
    root.querySelectorAll('[data-compare-id]').forEach((buttonNode) => {
      const active = selectedIds.has(String(buttonNode.dataset.compareId || ''));
      buttonNode.classList.toggle('is-active', active);
      buttonNode.setAttribute('aria-pressed', String(active));
      buttonNode.textContent = active ? 'Compared' : 'Compare';
      buttonNode.setAttribute('aria-label', active ? 'Remove from compare' : 'Add to compare');
    });
  }

  function recordRecommendationClick(product, variant, action, position) {
    state.lastAttributedProduct = product;
    markCartAssisted(product, 'recommendation_click', variant.id);
    if (action === 'view') {
      track(EVENTS.PRODUCT_CARD_CLICKED, product, {
        variant_id: variant.id,
        action,
        position
      });
    }
  }

  function markCartAssisted(product, touchpoint, variantId) {
    if (!['recommendation_click', 'add_to_cart', 'checkout'].includes(touchpoint)) return;
    const productHandle = String(product?.handle || '').slice(0, 150);
    fetch(config.cartUpdateUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          _techtic_assisted: 'true',
          _techtic_chat_session: state.sessionId,
          _techtic_chat_touchpoint: touchpoint,
          ...(productHandle ? { _techtic_chat_product: productHandle } : {}),
          ...(variantId ? { _techtic_chat_variant: String(variantId).slice(0, 30) } : {})
        }
      }),
      keepalive: true
    }).catch(() => {
      // Attribution must never interrupt the shopping experience.
    });
  }

  function renderSuggestions(suggestions) {
    clearSuggestions();
    const values = Array.isArray(suggestions) ? suggestions : [];
    values.slice(0, 3).forEach((suggestion) => {
      const clean = String(suggestion || '').trim();
      if (!clean) return;
      const suggestionButton = button(clean);
      suggestionButton.dataset.chatPrompt = clean;
      suggestionsNode.appendChild(suggestionButton);
    });
    suggestionsNode.scrollLeft = 0;
  }

  function clearSuggestions() {
    suggestionsNode.replaceChildren();
    suggestionsNode.scrollLeft = 0;
  }

  function setProductStatus(status, message, tone, product) {
    status.replaceChildren(element('span', '', message));
    status.dataset.tone = tone;
    status.hidden = false;
    if (product) {
      const handoff = createHandoffLink(product, 'verification');
      handoff.textContent = 'Ask on WhatsApp';
      status.appendChild(handoff);
    }
    scrollToLatest();
  }

  function renderRecoveryHandoff(afterNode, product, source) {
    const recovery = element('div', 'techtic-chat__recovery');
    const handoff = createHandoffLink(product, source);
    handoff.textContent = 'Chat to a person on WhatsApp';
    recovery.appendChild(handoff);
    afterNode.insertAdjacentElement('afterend', recovery);
  }

  function createHandoffLink(product, source) {
    const link = element('a', 'techtic-chat__handoff');
    link.href = whatsappUrl(product);
    link.target = '_blank';
    link.rel = 'noopener';
    link.dataset.chatHandoff = '';
    link.dataset.handoffSource = source || 'chat';
    if (product) {
      link.dataset.productId = product.productId || '';
      link.dataset.productHandle = product.handle || '';
      link.dataset.productTitle = product.title || '';
      link.dataset.productPrice = String(product.price ?? '');
    }
    return link;
  }

  function whatsappUrl(product) {
    try {
      const url = new URL(config.whatsappUrl, window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) return '#';
      if (product?.title && !url.searchParams.has('text')) {
        url.searchParams.set('text', 'Hi TechTic, please help me verify ' + product.title + '.');
      }
      return url.href;
    } catch {
      return config.whatsappUrl;
    }
  }

  function track(eventName, product, details) {
    const cleanDetails = cleanEventDetails(details);
    const event = 'techtic_chat_' + eventName;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      techtic_chat_session: state.sessionId,
      product_id: product?.productId || undefined,
      product_name: product?.title || undefined,
      product_handle: product?.handle || undefined,
      value: product?.price ?? undefined,
      currency: product ? config.currency : undefined,
      ...cleanDetails
    });

    if (!config.eventUrl) return;
    fetch(config.eventUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: eventName,
        sessionId: state.sessionId,
        path: root.dataset.pagePath,
        productHandle: product?.handle,
        productId: product?.productId,
        ...cleanDetails
      }),
      keepalive: true
    }).catch(() => {});
  }

  function cleanEventDetails(details) {
    if (!details || typeof details !== 'object') return {};
    return Object.fromEntries(Object.entries(details).filter(([, value]) => {
      return ['string', 'number', 'boolean'].includes(typeof value) && String(value).length <= 300;
    }));
  }

  function setBusy(isBusy) {
    sendButton.disabled = isBusy;
    form.setAttribute('aria-busy', String(isBusy));
    messagesNode.setAttribute('aria-busy', String(isBusy));
    if (clearButton) clearButton.disabled = isBusy || history.length === 0;
  }

  function updateClearButton() {
    if (clearButton) clearButton.disabled = state.sending || history.length === 0;
  }

  function resizeInput() {
    input.style.height = 'auto';
    input.style.height = Math.min(input.scrollHeight, 96) + 'px';
  }

  function scrollToLatest() {
    window.requestAnimationFrame(() => {
      messagesNode.scrollTop = messagesNode.scrollHeight;
    });
  }

  function announce(message) {
    if (!announcer) return;
    window.clearTimeout(announceTimer);
    announcer.textContent = '';
    announceTimer = window.setTimeout(() => {
      announcer.textContent = message;
    }, 30);
  }

  function welcomeText() {
    const key = root.dataset.productHandle ? 'productWelcome' : 'welcome';
    return text(key, 'Hi, I am Sam. What are you looking for?');
  }

  function text(key, fallback) {
    const value = String(root.dataset[key] || '').trim();
    return value && !/translation missing/i.test(value) ? value : fallback;
  }

  function availabilityLabel(variant) {
    if (variant.inventoryQuantity !== null) {
      return variant.inventoryQuantity + ' ' + pluralize(variant.inventoryQuantity, 'item available', 'items available');
    }
    if (variant.available === false) return 'Unavailable';
    if (variant.availabilityText) return variant.availabilityText;
    if (variant.available === true) return 'Available; exact quantity checked at add';
    return 'Live check required';
  }

  function variantOptionLabel(variant) {
    return variant.title + ' - ' + availabilityLabel(variant);
  }

  function verifiedLabel(date) {
    try {
      const now = new Date();
      const sameDay = now.toDateString() === date.toDateString();
      const formatted = new Intl.DateTimeFormat(config.locale, sameDay
        ? { hour: '2-digit', minute: '2-digit' }
        : { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }
      ).format(date);
      return 'Verified ' + formatted;
    } catch {
      return 'Verified recently';
    }
  }

  function formatMoney(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return 'Price unavailable';
    try {
      return new Intl.NumberFormat(config.locale, {
        style: 'currency',
        currency: config.currency,
        minimumFractionDigits: 2
      }).format(amount);
    } catch {
      return config.currency + ' ' + amount.toFixed(2);
    }
  }

  function formatCartMoney(cents) {
    return formatMoney(centsToAmount(cents));
  }

  function centsToAmount(cents) {
    const value = Number(cents);
    return Number.isFinite(value) ? value / 100 : 0;
  }

  function moneyValue(object, key) {
    if (!object || typeof object !== 'object') return null;
    const direct = object[key];
    if (direct && typeof direct === 'object') {
      const amount = firstNumber(direct.amount, direct.value);
      if (amount !== null) return amount;
    }
    const directNumber = firstNumber(direct);
    if (directNumber !== null) return directNumber;

    const aliases = key === 'compareAtPrice'
      ? [object.compare_at_price, object.comparePrice, object.originalPrice]
      : [object.currentPrice, object.priceAmount, object.sellingPrice];
    const aliasNumber = firstNumber(...aliases);
    if (aliasNumber !== null) return aliasNumber;

    const cents = key === 'compareAtPrice'
      ? firstNumber(object.compareAtPriceCents, object.compare_at_price_cents)
      : firstNumber(object.priceCents, object.price_cents);
    return cents === null ? null : cents / 100;
  }

  function normalizeImage(object) {
    if (!object || typeof object !== 'object') return { url: '', alt: '' };
    const imageObject = object.image && typeof object.image === 'object'
      ? object.image
      : object.featuredImage && typeof object.featuredImage === 'object'
        ? object.featuredImage
        : {};
    return {
      url: safeImageUrl(firstString(
        object.imageUrl,
        typeof object.image === 'string' ? object.image : '',
        imageObject.url,
        imageObject.src,
        object.featuredImageUrl
      )),
      alt: firstString(object.imageAlt, imageObject.alt, imageObject.altText)
    };
  }

  function safeImageUrl(value) {
    if (!value) return '';
    try {
      const url = new URL(String(value).startsWith('//') ? 'https:' + value : value, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    } catch {
      return '';
    }
  }

  function safeUrl(value, fallback) {
    if (!value) return fallback;
    try {
      const url = new URL(value, window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) return fallback;
      return url.origin === window.location.origin
        ? url.pathname + url.search + url.hash
        : url.href;
    } catch {
      return fallback;
    }
  }

  function withVariant(urlValue, variantId) {
    if (!urlValue) return '';
    try {
      const url = new URL(urlValue, window.location.origin);
      if (variantId) url.searchParams.set('variant', variantId);
      return url.origin === window.location.origin
        ? url.pathname + url.search + url.hash
        : url.href;
    } catch {
      return urlValue;
    }
  }

  function handleFromUrl(url) {
    const match = String(url || '').match(/\/products\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function apiUrl(pathValue) {
    const path = String(pathValue || '').trim();
    if (/^https?:\/\//i.test(path)) return path;
    const base = String(root.dataset.apiBase || '').replace(/\/+$/, '');
    return base + '/' + path.replace(/^\/+/, '');
  }

  function shopifyRoute(path) {
    const routeRoot = String(window.Shopify?.routes?.root || '/');
    return routeRoot.replace(/\/?$/, '/') + String(path || '').replace(/^\/+/, '');
  }

  function productFromDataset(node) {
    if (!node?.dataset?.productHandle && !node?.dataset?.productId) return null;
    return {
      productId: node.dataset.productId,
      handle: node.dataset.productHandle,
      title: node.dataset.productTitle,
      price: firstNumber(node.dataset.productPrice)
    };
  }

  function closestElement(event, selector) {
    return event.target instanceof Element ? event.target.closest(selector) : null;
  }

  function element(tagName, className, content) {
    const node = document.createElement(tagName);
    if (className) node.className = className;
    if (content !== undefined && content !== null) node.textContent = String(content);
    return node;
  }

  function button(label, className) {
    const node = element('button', className, label);
    node.type = 'button';
    return node;
  }

  function getFocusableElements(container) {
    return Array.from(container.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((node) => node.getAttribute('aria-hidden') !== 'true' && node.getClientRects().length > 0);
  }

  function arrayValue(value) {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.nodes)) return value.nodes;
    if (Array.isArray(value?.edges)) return value.edges.map((edge) => edge?.node).filter(Boolean);
    return [];
  }

  function compactObjects(values) {
    return values.filter((value) => value && typeof value === 'object' && !Array.isArray(value));
  }

  function firstArrayFrom(candidates, keys) {
    for (const candidate of candidates) {
      for (const key of keys) {
        const value = arrayValue(candidate[key]);
        if (value.length) return value;
      }
    }
    return [];
  }

  function firstString(...values) {
    for (const value of values) {
      if (typeof value === 'string' || typeof value === 'number') {
        const clean = String(value).trim();
        if (clean) return clean;
      }
    }
    return '';
  }

  function firstNumber(...values) {
    for (const value of values) {
      if (value === null || value === undefined || value === '') continue;
      const number = Number(value);
      if (Number.isFinite(number)) return number;
    }
    return null;
  }

  function firstBoolean(...values) {
    for (const value of values) {
      if (typeof value === 'boolean') return value;
      if (value === 1 || value === '1') return true;
      if (value === 0 || value === '0') return false;
      if (typeof value !== 'string') continue;
      const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, '_');
      if (['true', 'yes', 'available', 'in_stock'].includes(normalized)) return true;
      if (['false', 'no', 'unavailable', 'out_of_stock', 'sold_out'].includes(normalized)) return false;
    }
    return null;
  }

  function dataBoolean(value, fallback) {
    const parsed = firstBoolean(value);
    return parsed === null ? fallback : parsed;
  }

  function clampInteger(value, minimum, maximum, fallback) {
    const number = Math.floor(Number(value));
    if (!Number.isFinite(number)) return fallback;
    return Math.min(maximum, Math.max(minimum, number));
  }

  function validDate(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  function pluralize(count, singular, plural) {
    return Number(count) === 1 ? singular : plural;
  }

  function shopifyId(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const gidMatch = raw.match(/\/(\d+)$/);
    return gidMatch ? gidMatch[1] : raw;
  }

  function createRequestId() {
    return (window.crypto?.randomUUID?.() || Date.now() + '-' + Math.random().toString(36).slice(2))
      .replace(/[^a-z0-9_-]/gi, '');
  }

  function getSessionId() {
    const existing = sessionStorage.getItem('techtic_chat_session_id_v3');
    if (existing) return existing;
    const id = createRequestId();
    sessionStorage.setItem('techtic_chat_session_id_v3', id);
    return id;
  }

  function restoreHistory() {
    try {
      const saved = JSON.parse(sessionStorage.getItem('techtic_chat_history_v3') || '[]');
      return Array.isArray(saved)
        ? saved.filter((item) => {
          return item && ['user', 'assistant'].includes(item.role) && typeof item.content === 'string';
        }).slice(-10)
        : [];
    } catch {
      return [];
    }
  }

  function saveHistory() {
    try {
      sessionStorage.setItem('techtic_chat_history_v3', JSON.stringify(history.slice(-10)));
    } catch {
      // Session storage is optional.
    }
  }

  async function readJson(response) {
    const textValue = await response.text();
    if (!textValue) return {};
    try {
      return JSON.parse(textValue);
    } catch {
      return {};
    }
  }

  function parseEventData(value) {
    const clean = String(value || '').trim();
    if (!clean) return '';
    try {
      return JSON.parse(clean);
    } catch {
      return clean;
    }
  }

  function sseEventType(eventName, payload) {
    const explicit = String(eventName || '').trim().toLowerCase();
    if (explicit && explicit !== 'message') return explicit;
    if (payload && typeof payload === 'object') {
      return firstString(payload.type, payload.event, 'message').toLowerCase();
    }
    return 'message';
  }

  function eventValue(payload, keys) {
    if (typeof payload === 'string') return payload;
    if (!payload || typeof payload !== 'object') return '';
    for (const key of keys) {
      const value = payload[key];
      if (typeof value === 'string' || typeof value === 'number') return String(value);
      if (value && typeof value === 'object') {
        const nested = firstString(value.content, value.text, value.message);
        if (nested) return nested;
      }
    }
    if (payload.data !== undefined) return eventValue(payload.data, keys);
    if (payload.payload !== undefined) return eventValue(payload.payload, keys);
    return '';
  }

  function unwrapSsePayload(payload, type) {
    if (!payload || typeof payload !== 'object') return payload;
    if (payload.type === type || payload.event === type) {
      return payload.payload || payload.data || payload.response || payload;
    }
    return payload;
  }

  function findLineBreak(value) {
    const index = value.search(/\r?\n/);
    if (index < 0) return null;
    return {
      index,
      length: value[index] === '\r' && value[index + 1] === '\n' ? 2 : 1
    };
  }

  function publicReason(error) {
    const message = error instanceof Error ? error.message : String(error || '');
    return message.replace(/sk-[A-Za-z0-9_-]+/g, '[redacted]').slice(0, 300);
  }

  class StreamUnavailableError extends Error {}
  class VerificationError extends Error {}
})();
